import React, { useCallback, useEffect, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

const PROVIDER = {
  openai: { label: 'OpenAI', color: '#34D399' },
  google: { label: 'Google Gemini', color: '#60A5FA' },
} as const;

interface Balance {
  provider: string;
  credits_added: number;
  spent: number;
  remaining: number;
  daily_burn: number;
  days_left: number | null;
  calls: number;
}
interface DayRow { date: string; openai: number; google: number; total: number; calls: number; tokens: number }
interface BreakdownRow { name: string; provider: string; calls: number; tokens: number; cost: number; unpriced: number }
interface Resp { balances: Balance[]; daily: DayRow[]; by_model: BreakdownRow[]; by_feature: BreakdownRow[] }

const RANGES = [7, 14, 30, 60] as const;

const usd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;
const compactTokens = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n);

function AICostsTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/ai-usage?days=${days}`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData((d.data ?? d) as Resp);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  const th: React.CSSProperties = {
    textAlign: 'right', padding: '11px 12px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const td: React.CSSProperties = {
    textAlign: 'right', padding: '11px 12px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const card: React.CSSProperties = {
    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px 20px',
  };

  const chartData = (data?.daily ?? []).slice().reverse().map((r) => ({
    day: new Date(r.date + 'T00:00:00Z').toLocaleDateString('en-GB',
      { day: '2-digit', month: 'short', timeZone: 'Asia/Karachi' }),
    OpenAI: +r.openai.toFixed(4),
    'Google Gemini': +r.google.toFixed(4),
  }));

  const unpricedTotal = (data?.by_model ?? []).reduce((a, m) => a + m.unpriced, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: C.text }}>AI costs</h1>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${days === r ? C.accent : C.border}`,
                background: days === r ? C.accent : 'transparent',
                color: days === r ? '#fff' : C.sub,
              }}
            >{r}d</button>
          ))}
        </div>
      </div>
      <p style={{ color: C.sub, margin: '6px 0 18px', fontSize: 13.5, lineHeight: 1.6, maxWidth: 820 }}>
        Metered from the token counts each provider returns on every call. Remaining credit is what
        you topped up minus what we measured since metering began on 1 Sep 2026, so it is an estimate,
        not a reading from the provider. Reconcile against the OpenAI and Google dashboards now and then.
      </p>

      {error && <div style={{ color: '#F87171', fontSize: 13.5, marginBottom: 12 }}>{error}</div>}
      {loading && !data && <div style={{ color: C.sub, fontSize: 13.5 }}>Loading…</div>}

      {data && (
        <>
          {/* Balances */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 20 }}>
            {data.balances.map((b) => {
              const meta = PROVIDER[b.provider as keyof typeof PROVIDER] ?? { label: b.provider, color: C.sub };
              const pct = b.credits_added > 0 ? Math.max(0, Math.min(100, (b.remaining / b.credits_added) * 100)) : 0;
              const low = pct < 20;
              return (
                <div key={b.provider} style={{ ...card, borderLeft: `4px solid ${meta.color}` }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <strong style={{ fontSize: 15, color: C.text }}>{meta.label}</strong>
                    <span style={{ color: C.sub, fontSize: 12.5 }}>{b.calls.toLocaleString()} calls</span>
                  </div>
                  <div style={{ fontSize: 30, fontWeight: 800, marginTop: 10, color: low ? '#F87171' : meta.color }}>
                    {usd(b.remaining)}
                  </div>
                  <div style={{ color: C.sub, fontSize: 12.5, marginTop: 2 }}>
                    left of {usd(b.credits_added)} &middot; {usd(b.spent)} spent
                  </div>
                  <div style={{ height: 6, background: C.panelAlt, borderRadius: 3, marginTop: 12, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: low ? '#F87171' : meta.color }} />
                  </div>
                  <div style={{ color: C.sub, fontSize: 12.5, marginTop: 10 }}>
                    {b.daily_burn > 0
                      ? <>Burning {usd(b.daily_burn)}/day{b.days_left != null && <> &middot; <strong style={{ color: low ? '#F87171' : C.text }}>~{Math.floor(b.days_left)} days left</strong></>}</>
                      : 'No spend recorded yet'}
                  </div>
                </div>
              );
            })}
          </div>

          {unpricedTotal > 0 && (
            <div style={{ ...card, borderLeft: '4px solid #FBBF24', marginBottom: 20, fontSize: 13.5, color: C.sub }}>
              <strong style={{ color: '#FBBF24' }}>{unpricedTotal} calls from unpriced models.</strong>{' '}
              Their tokens are counted but cost shows as $0. Add the model to the price table in
              <code style={{ color: C.text, margin: '0 4px' }}>internal/aiusage/aiusage.go</code>
              to include them.
            </div>
          )}

          {/* Daily spend */}
          <div style={{ ...card, height: isMobile ? 250 : 300, marginBottom: 22, padding: '18px 18px 8px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ai-openai" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#34D399" stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="ai-google" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: C.sub, fontSize: 11.5 }} tickLine={false} axisLine={{ stroke: C.border }} minTickGap={24} />
                <YAxis tick={{ fill: C.sub, fontSize: 11.5 }} tickLine={false} axisLine={false} width={58}
                  tickFormatter={(v: number) => `$${v}`} />
                <Tooltip
                  formatter={(v) => `$${Number(v).toFixed(4)}`}
                  contentStyle={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 12.5 }}
                  labelStyle={{ color: C.text, fontWeight: 700 }}
                />
                <Legend wrapperStyle={{ fontSize: 12.5 }} iconType="circle" />
                <Area type="monotone" dataKey="OpenAI" stackId="1" stroke="#34D399" strokeWidth={2} fill="url(#ai-openai)" />
                <Area type="monotone" dataKey="Google Gemini" stackId="1" stroke="#60A5FA" strokeWidth={2} fill="url(#ai-google)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdowns */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            {([['By feature', data.by_feature], ['By model', data.by_model]] as const).map(([title, rows]) => (
              <div key={title}>
                <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 10px' }}>{title}</h2>
                <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 380 }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: 'left' }}>{title === 'By model' ? 'Model' : 'Feature'}</th>
                        <th style={th}>Calls</th>
                        <th style={th}>Tokens</th>
                        <th style={th}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.name}>
                          <td style={{ ...td, textAlign: 'left' }}>
                            {r.name}
                            {r.unpriced > 0 && <span style={{ marginLeft: 6, fontSize: 10.5, color: '#FBBF24', fontWeight: 700 }}>UNPRICED</span>}
                          </td>
                          <td style={td}>{r.calls.toLocaleString()}</td>
                          <td style={td}>{compactTokens(r.tokens)}</td>
                          <td style={{ ...td, color: r.cost > 0 ? '#34D399' : C.sub }}>{usd(r.cost)}</td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: C.sub }}>Nothing recorded yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default AICostsTab;
